import {AgentError} from "./AgentError";
import {AgentSidebar} from "./AgentSidebar";
import "./activity.css";
import "./agent-sidebar.css";
import{useCallback,useEffect,useMemo,useRef,useState}from"react";
import{agentControlV2Api}from"../../lib/agentControlV2Api";
import type{Mode}from"../../lib/types";
import type{V2InputAttachment,V2Message,V2RunState,V2SessionSummary}from"../../lib/agentControlV2Api";
import{authApi}from"../../lib/authApi";

/**
 * KNSAIX Agent Control — conversation-first product workspace.
 *
 * Three-pane layout: session sidebar / chat center / control panel.
 * Sessions are lazily created on the first message; the user never fills
 * engineering forms. ASK / PLAN / ACT / TASK routing is automatic with a
 * visible chip and manual override; explicit safety constraints in the
 * message always win over automatic classification.
 */

const WORKSPACE_CWD="/home/kns/Projeler/knsaix";


const SAFE_CONSTRAINT=/(read[ -]?only|do\s*n[o']t\s+modify|don['’]t\s+modify|plan\s+only|do\s*n[o']t\s+execute|don['’]t\s+execute|no\s+changes|analysis\s+only)/i;
const ACT_CUE=/(implement|patch\b|\bfix\b|refactor|write\s+.*code|create\s+.*file|update\s+.*file|edit\s+.*file|run\s+the\s+tests|add\s+.*feature|delete|migrate|deploy|apply)/i;
const TASK_CUE=/(multi[- ]step|end[- ]to[- ]end|\be2e\b|autonom(ous|ly)|complete\s+task|build\s+and\s+verify|full\s+workflow|step\s+by\s+step\s+until)/i;
const PLAN_CUE=/(\bplan\b|design|architecture|investigat\w*|analy[sz]e|approach|strategy|roadmap|how\s+(should|would|can)|what\s+if|tradeoffs?)/i;

export function classifyMode(text:string):Mode{
 if(SAFE_CONSTRAINT.test(text))return "PLAN" as Mode;
 if(TASK_CUE.test(text))return "TASK" as Mode;
 if(ACT_CUE.test(text))return "ACT" as Mode;
 if(PLAN_CUE.test(text))return "PLAN" as Mode;
 return "ASK" as Mode;
}

export function titleFrom(text:string):string{
 let t=text
  .replace(/```[\s\S]*?```/g," ")
  .replace(/\s+/g," ")
  .trim();

 t=t
  .replace(/^(please|lütfen)\s+/i,"")
  .replace(/^(can|could|would)\s+you\s+/i,"")
  .replace(/^(bana|şunu|şunu bir)\s+/i,"")
  .replace(/^(reply with exactly|tell me only)\s*:?\s*/i,"");

 const first=t.split(/(?<=[.!?])\s+/)[0]?.trim()||t;
 const clean=first.replace(/[.!?]+$/,"").trim();
 const words=clean.split(/\s+/).filter(Boolean);
 const compact=words.length>10
  ?`${words.slice(0,10).join(" ")}…`
  :clean;

 return compact.length>64
  ?`${compact.slice(0,64).trimEnd()}…`
  :compact||"New chat";
}

interface TaskActivity{
 activity_type:string;
 status:string;
 label:string;
 detail:string|null;
 source:string;
 worker_run_id:string|null;
 occurred_at:string;
}

interface TraceRow{
 id:string;
 work_item_id:string;
 event_type:string;
 payload:Record<string,unknown>;
 occurred_at:string;
 activity?:TaskActivity|null;
}


interface ApprovalRow extends Record<string,unknown>{
 approval_id?:string;
 status?:string;
 kind?:string;
 reason?:string;
}

type RightTab="Run"|"Trace"|"Files"|"Context"|"Approvals";

const STATUS_LABEL:Record<string,string>={
 run_state:"Status",session_start:"Agent started",iteration:"Iteration",
 assistant_output:"Output",usage:"Usage",session_end:"Finished",
 done:"Done",run_result:"Result",session_bound:"Cline session bound",
 tool_call:"Tool",read_file:"Read file",edited_file:"Edited file",
 created_file:"Created file",command:"Command",search:"Search",
};


/* KNSAIX_AGENT_CHAT_PRODUCT_COMPLETION_20260829 */
async function copyTextValue(text:string):Promise<boolean>{
 try{
  if(navigator.clipboard&&window.isSecureContext){
   await navigator.clipboard.writeText(text);
   return true;
  }
 }catch{/* fall through */}

 try{
  const area=document.createElement("textarea");
  area.value=text;
  area.setAttribute("readonly","");
  area.style.position="fixed";
  area.style.opacity="0";
  document.body.appendChild(area);
  area.select();
  const ok=document.execCommand("copy");
  area.remove();
  return ok;
 }catch{
  return false;
 }
}



function MessageInputAttachments({attachments}:{attachments?:V2InputAttachment[]}){
 if(!attachments?.length)return null;
 const size=(bytes:number)=>bytes<1024?`${bytes} B`:bytes<1024*1024?`${(bytes/1024).toFixed(1)} KB`:`${(bytes/(1024*1024)).toFixed(1)} MB`;
 return(
  <div className="kg-input-message-files">
   {attachments.map(file=>(
    <div className="kg-input-message-file" key={file.id} title={file.mime_type}>
     <strong>{file.name}</strong><small>{size(file.size)}</small>
    </div>
   ))}
  </div>
 );
}


function MessageArtifacts({
 artifacts,
}:{
 artifacts?:Array<{
  path:string;
  name:string;
  mime_type:string;
  size:number;
  previewable:boolean;
  download_url:string;
  preview_url:string|null;
 }>;
}){
 if(!artifacts?.length){
  return null;
 }

 const formatSize=(bytes:number)=>{
  if(bytes<1024)return `${bytes} B`;
  if(bytes<1024*1024){
   return `${(bytes/1024).toFixed(1)} KB`;
  }
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
 };

 return (
  <div className="kg-message-artifacts">
   {artifacts.map((artifact)=>{
    const isImage=
     artifact.mime_type.startsWith(
      "image/"
     );

    return (
     <div
      className="kg-artifact-card"
      key={`${artifact.path}-${artifact.size}`}
     >
      {isImage&&artifact.preview_url&&(
       <a
        href={artifact.download_url}
        target="_blank"
        rel="noreferrer"
        className="kg-artifact-image-link"
       >
        <img
         src={artifact.preview_url}
         alt={artifact.name}
         className="kg-artifact-image"
         loading="lazy"
        />
       </a>
      )}

      <div className="kg-artifact-meta">
       <div className="kg-artifact-name">
        {artifact.name}
       </div>

       <div className="kg-artifact-detail">
        {artifact.mime_type}
        {" · "}
        {formatSize(artifact.size)}
       </div>
      </div>

      <div className="kg-artifact-buttons">
       {artifact.preview_url&&!isImage&&(
        <a
         href={artifact.preview_url}
         target="_blank"
         rel="noreferrer"
        >
         Önizle
        </a>
       )}

       <a
        href={artifact.download_url}
        target="_blank"
        rel="noreferrer"
       >
        İndir
       </a>
      </div>
     </div>
    );
   })}
  </div>
 );
}

function MessageBody({content}:{content:string}){
 // agent-inline-media-renderer-v3-20260829

 const renderSegment=(
  text:string,
  prefix:string,
 )=>{
  const urlPattern=/(https?:\/\/[^\s<>"']+)/gi;
  const chunks=text.split(urlPattern);

  return chunks.map((chunk,index)=>{
   if(!/^https?:\/\//i.test(chunk)){
    return (
     <span
      className="acw-rich-text-piece"
      key={`${prefix}-text-${index}`}
     >
      {chunk}
     </span>
    );
   }

   let url=chunk;
   let trailing="";

   while(
    url.length>0
    && /[),.;!?]$/.test(url)
   ){
    trailing=
     url.slice(-1)+trailing;

    url=url.slice(0,-1);
   }

   const imageExtension=
    /\.(?:png|jpe?g|webp|gif|bmp|tiff?)(?:$|[?#])/i
     .test(url);

   const signedGoogleObject=
    /^https:\/\/storage\.googleapis\.com\//i
     .test(url)
    && /(?:X-Goog-Signature|GoogleAccessId|Signature)=/i
     .test(url);

   const imageUrl=
    imageExtension
    || signedGoogleObject;

   if(imageUrl){
    return (
     <span
      className="kg-inline-artifact-wrap"
      key={`${prefix}-image-${index}`}
     >
      <a
       href={url}
       target="_blank"
       rel="noreferrer"
       className="kg-inline-image-link"
      >
       <img
        src={url}
        alt="Agent tarafından oluşturulan görsel"
        className="kg-inline-image"
        loading="lazy"
        onError={event=>{
         const image=event.currentTarget;

         image.style.display="none";

         image
          .closest(
           ".kg-inline-artifact-wrap"
          )
          ?.classList.add(
           "kg-inline-artifact-failed"
          );
        }}
       />

       <span
        className="kg-inline-image-fallback"
       >
        Görsel önizlemesi açılamadı · aç
       </span>
      </a>

      <span
       className="kg-inline-artifact-actions"
      >
       <a
        href={url}
        target="_blank"
        rel="noreferrer"
       >
        Aç
       </a>

       <button
        type="button"
        onClick={()=>{
         void navigator.clipboard.writeText(
          url
         );
        }}
       >
        Bağlantıyı kopyala
       </button>
      </span>

      {trailing}
     </span>
    );
   }

   const shortLabel=(()=>{
    try{
     const parsed=new URL(url);

     const path=
      parsed.pathname.length>42
       ?`${parsed.pathname.slice(0,39)}…`
       :parsed.pathname;

     return `${parsed.hostname}${path}`;
    }catch{
     return url.length>68
      ?`${url.slice(0,65)}…`
      :url;
    }
   })();

   return (
    <span
     key={`${prefix}-link-${index}`}
    >
     <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="kg-inline-link"
      title={url}
     >
      {shortLabel}
     </a>

     {trailing}
    </span>
   );
  });
 };

 const nodes:React.ReactNode[]=[];
 const fence=/```([\w-]*)\n?([\s\S]*?)```/g;

 let cursor=0;
 let match:RegExpExecArray|null;
 let index=0;

 while(
  (match=fence.exec(content))!==null
 ){
  if(match.index>cursor){
   nodes.push(
    <span
     className="acw-rich-text-piece"
     key={`text-${index}`}
    >
     {renderSegment(
      content.slice(
       cursor,
       match.index,
      ),
      `segment-${index}`,
     )}
    </span>
   );

   index++;
  }

  nodes.push(
   <pre
    className="acw-code-block"
    key={`code-${index}`}
   >
    <code>
     {match[2]}
    </code>
   </pre>
  );

  index++;

  cursor=
   match.index
   +match[0].length;
 }

 if(cursor<content.length){
  nodes.push(
   <span
    className="acw-rich-text-piece"
    key={`text-${index}`}
   >
    {renderSegment(
     content.slice(cursor),
     `segment-${index}`,
    )}
   </span>
  );
 }

 return (
  <div className="acw-rich-text">
   {nodes.length
    ?nodes
    :renderSegment(
      content,
      "plain",
     )
   }
  </div>
 );
}


export function AgentWorkspace({onSignOut}:{onSignOut:()=>void}){
 const [sessions,setSessions]=useState<V2SessionSummary[]|null>(null);
 const [activeId,setActiveId]=useState<string|null>(null);
 const [messages,setMessages]=useState<V2Message[]>([]);
 const [run,setRun]=useState<V2RunState|null>(null);
 const [trace,setTrace]=useState<TraceRow[]>([]);
 const [approvals,setApprovals]=useState<ApprovalRow[]>([]);
 const [draft,setDraft]=useState("");
 const [modeOverride,setModeOverride]=useState<Mode|null>(null);
 const [pendingAttachments,setPendingAttachments]=useState<V2InputAttachment[]>([]);
 const [uploadingAttachments,setUploadingAttachments]=useState(false);
 const composerFileRef=useRef<HTMLInputElement|null>(null);
 const [modelOptions,setModelOptions]=useState<
  Array<{id:string;context_window:number|null}>
 >([]);

 /* KNSAIX_IZLOTASK_UI_V1 */
 const [localModelOptions,setLocalModelOptions]=useState<
  Array<{
   id:string;
   context_window:number|null;
   upstream_model?:string|null;
   provider_key?:string|null;
  }>
 >([]);
 const [pendingModel,setPendingModel]=useState("AUTO");
 const [pendingProfile,setPendingProfile]=useState<
  "FAST"|"BALANCED"|"DEEP"
 >("BALANCED");
 const [busy,setBusy]=useState(false);
 const [error,setError]=useState<string|null>(null);
 const [notice,setNotice]=useState<string|null>(null);
 const [search,setSearch]=useState("");
 const composerRef=useRef<HTMLTextAreaElement|null>(null);
 const [sessionView,setSessionView]=useState<"active"|"archived">("active");

 const [editingId,setEditingId]=useState<string|null>(null);

 const [editingTitle,setEditingTitle]=useState("");
 const [rightOpen,setRightOpen]=useState(false);
 const [leftOpen,setLeftOpen]=useState(false);
 const [sidebarCollapsed,setSidebarCollapsed]=useState(false);
 const [searchOpen,setSearchOpen]=useState(false);
 const [accountOpen,setAccountOpen]=useState(false);
 const [statusOpen,setStatusOpen]=useState(false);
 const [messageMenuId,setMessageMenuId]=useState<string|null>(null);

 // KNSAIX_MESSAGE_TIMESTAMP_V1
 const [expandedTimeId,setExpandedTimeId]=
  useState<string|null>(null);
 const [identity,setIdentity]=useState<Awaited<ReturnType<typeof authApi.me>>|null>(null);
 const [tab,setTab]=useState<RightTab>("Run");
 const pollRef=useRef<number|undefined>(undefined);
 const syncSeqRef=useRef(0);
 const bottomRef=useRef<HTMLDivElement|null>(null);
 const searchRef=useRef<HTMLInputElement|null>(null);

 // Continuation URL carries only the canonical KNSAIX session UUID.
 // It is not authorization; server auth, IAM and owner guards remain authoritative.
 const initialSessionRef=useRef<string|null>(
  new URLSearchParams(window.location.search).get("session"),
 );

 const syncContinuationUrl=useCallback((id:string|null)=>{
  const url=new URL(window.location.href);

  url.searchParams.set("workspace","agent");

  if(id){
   url.searchParams.set("session",id);
  }else{
   url.searchParams.delete("session");
  }

  url.hash="";

  window.history.replaceState(
   window.history.state,
   "",
   `${url.pathname}${url.search}`,
  );
 },[]);

 const activeMode:Mode=modeOverride??classifyMode(draft);

 const preferenceSession=
  (sessions??[]).find(row=>row.id===activeId)??null;

 const selectedModel=
  activeId
   ?(preferenceSession?.preferred_model_key??"AUTO")
   :pendingModel;

 const selectedProfile=(
  activeId
   ?(preferenceSession?.execution_profile??"BALANCED")
   :pendingProfile
 ) as "FAST"|"BALANCED"|"DEEP";


 const isIzloTask=
  preferenceSession?.execution_scope==="ISOLATED_LOCAL";

 useEffect(()=>{
  let cancelled=false;
  void agentControlV2Api.models()
   .then(rows=>{
    if(!cancelled)setModelOptions(rows);
   })
   .catch(()=>{});
  return()=>{cancelled=true;};
 },[]);


 useEffect(()=>{
  let cancelled=false;

  void agentControlV2Api.localModels()
   .then(rows=>{
    if(!cancelled){
     setLocalModelOptions(rows);
    }
   })
   .catch(()=>{
    if(!cancelled){
     setLocalModelOptions([]);
    }
   });

  return()=>{
   cancelled=true;
  };
 },[]);
 const running=run?.status==="RUNNING"||run?.status==="QUEUED";

 const loadSessions=useCallback(async()=>{
  try{
   const rows=await agentControlV2Api.listSessions(sessionView);
   setSessions(rows);setError(null);

   setActiveId(cur=>{
    if(sessionView==="archived")return null;

    const requested=initialSessionRef.current;

    if(requested){
     initialSessionRef.current=null;

     if(rows.some(row=>row.id===requested)){
      return requested;
     }
    }

    if(cur&&rows.some(row=>row.id===cur))return cur;
    return rows[0]?.id??null;
   });
  }catch(e){
   const msg=e instanceof Error?e.message:"load_failed";
   setError(msg==="access_management_denied"
    ?"Your account does not currently have access to Agent Control."
    :msg);
  }
 },[sessionView]);

 const refreshActive=useCallback(async(
 id:string,
 withTrace=false,
 preserveError=false,
)=>{
 // KNSAIX_PERSISTENT_SUBMIT_ERROR_V1
 try{
  await agentControlV2Api
   .sync(id,0)
   .catch(()=>null);

  const [d,liveTrace]=await Promise.all([
   agentControlV2Api.getSession(id),
   agentControlV2Api
    .trace(id)
    .catch(()=>null),
  ]);

  setMessages(d.messages??[]);
  setRun(d.run??null);

  if(!preserveError){
   setError(null);
  }

  if(liveTrace){
   setTrace(
    liveTrace as TraceRow[]
   );
  }else if(withTrace){
   try{
    setTrace(
     await agentControlV2Api
      .trace(id) as TraceRow[]
    );
   }catch{
    /* lazy */
   }
  }
 }catch(e){
  setError(
   e instanceof Error
    ?e.message
    :"load_failed"
  );
 }
},[]);

useEffect(()=>{void loadSessions();},[loadSessions]);

 useEffect(()=>{
  let cancelled=false;
  void authApi.me()
   .then(me=>{
    if(!cancelled)setIdentity(me);
   })
   .catch(()=>{
    if(!cancelled)setIdentity(null);
   });
  return()=>{cancelled=true;};
 },[]);

 useEffect(()=>{
  const closeMenus=(event:KeyboardEvent)=>{
   if((event.metaKey||event.ctrlKey)&&event.key.toLowerCase()==="k"){
    event.preventDefault();
    setSidebarCollapsed(false);
    setLeftOpen(true);
    setSearchOpen(true);
    window.requestAnimationFrame(()=>searchRef.current?.focus());
    return;
   }
   if(event.key==="Escape"){
    setAccountOpen(false);
    setStatusOpen(false);
    setMessageMenuId(null);
   }
  };
  window.addEventListener("keydown",closeMenus);
  return()=>window.removeEventListener("keydown",closeMenus);
 },[]);

 useEffect(()=>{
  syncContinuationUrl(activeId);
 },[activeId,syncContinuationUrl]);

 // UX finish pack: keep drawer state coherent across breakpoints.
 useEffect(()=>{
  const desktop=window.matchMedia("(min-width:1101px)");

  const onBreakpoint=()=>{
   setLeftOpen(false);
   setRightOpen(false);
  };

  desktop.addEventListener("change",onBreakpoint);

  return()=>{
   desktop.removeEventListener("change",onBreakpoint);
  };
 },[]);

 useEffect(()=>{
  const onKeyboard=(event:KeyboardEvent)=>{
   if(event.key==="Escape"){
    setLeftOpen(false);
    setRightOpen(false);
    return;
   }

   if(
    (event.metaKey||event.ctrlKey)
    &&event.key.toLowerCase()==="k"
   ){
    event.preventDefault();
    setRightOpen(false);
    setLeftOpen(true);

    window.requestAnimationFrame(()=>{
     searchRef.current?.focus();
    });
   }
  };

  window.addEventListener("keydown",onKeyboard);

  return()=>{
   window.removeEventListener("keydown",onKeyboard);
  };
 },[]);

 // Focused polling only while a run is active (canonical sync endpoint).
 useEffect(()=>{
  if(!activeId)return;

  window.clearInterval(pollRef.current);

  if(!running){
   void refreshActive(activeId,true);
   return;
  }

  syncSeqRef.current=0;

  const poll=()=>{
   void agentControlV2Api.sync(activeId,syncSeqRef.current).then(async snap=>{
    syncSeqRef.current=Math.max(
     syncSeqRef.current,
     Number(snap.latest_seq??syncSeqRef.current),
    );

    const [d,liveTrace]=await Promise.all([
     agentControlV2Api
      .getSession(activeId)
      .catch(()=>null),

     agentControlV2Api
      .trace(activeId)
      .catch(()=>null),
    ]);

    if(d){
     setMessages(d.messages??[]);
     setRun(d.run??null);
    }

    if(liveTrace){
     setTrace(
      liveTrace as TraceRow[]
     );
    }

    if(
     snap.status==="COMPLETED"||
     snap.status==="FAILED"||
     snap.status==="CANCELLED"||
     snap.status==="UNREACHABLE"
    ){
     // sync is authoritative for worker terminal state.
     // Do not leave the UI stuck on a stale RUNNING session snapshot.
     setRun(prev=>({
      ...(d?.run??prev??{
       worker_run_id:null,
       cline_session_id:null,
       result_text:null,
       error_message:null,
      }),
      status:snap.status,
      result_text:snap.result_text??d?.run?.result_text??prev?.result_text??null,
      error_message:snap.error_message??d?.run?.error_message??prev?.error_message??null,
     }));

     window.clearInterval(pollRef.current);
     void refreshActive(activeId,true);
    }
   }).catch(()=>{/* transient */});
  };

  poll();
  pollRef.current=window.setInterval(poll,4000);

  return()=>window.clearInterval(pollRef.current);
 },[activeId,running,refreshActive]);

 useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});},[messages.length,run?.status]);

 useEffect(()=>{
  const el=composerRef.current;
  if(!el)return;
  el.style.height="0px";
  const height=Math.min(Math.max(el.scrollHeight,44),180);
  el.style.height=`${height}px`;
 },[draft]);


 useEffect(()=>{
  let cancelled=false;
  if(!activeId){setPendingAttachments([]);return()=>{cancelled=true;};}
  void agentControlV2Api.pendingAttachments(activeId)
   .then(rows=>{if(!cancelled)setPendingAttachments(rows);})
   .catch(()=>{if(!cancelled)setPendingAttachments([]);});
  return()=>{cancelled=true;};
 },[activeId]);

 async function addComposerFiles(files:FileList|null){
  if(!files?.length||busy||running||uploadingAttachments)return;
  const selected=Array.from(files);
  if(pendingAttachments.length+selected.length>8){setError("En fazla 8 dosya eklenebilir.");return;}
  const existing=pendingAttachments.reduce((sum,file)=>sum+file.size,0);
  const incoming=selected.reduce((sum,file)=>sum+file.size,0);
  if(selected.some(file=>file.size<=0||file.size>8*1024*1024)){setError("Her dosya en fazla 8 MB olabilir.");return;}
  if(existing+incoming>20*1024*1024){setError("Toplam ek boyutu en fazla 20 MB olabilir.");return;}
  setUploadingAttachments(true);setError(null);
  try{
   let sid=activeId;
   if(!sid){
    const created=await agentControlV2Api.createSession({
     title:titleFrom(draft.trim()||selected[0]?.name||"New chat"),
     workspace_cwd:WORKSPACE_CWD,memory_scope:"PERSONAL",mode:"ACT",
     preferred_model_key:pendingModel==="AUTO"?null:pendingModel,
     execution_profile:pendingProfile,
    });
    sid=created.session_id;setActiveId(sid);await loadSessions();
   }
   for(const file of selected){
    const uploaded=await agentControlV2Api.uploadAttachment(sid,file);
    setPendingAttachments(prev=>[...prev,uploaded]);
   }
   setNotice(selected.length===1?`Dosya eklendi: ${selected[0].name}`:`${selected.length} dosya eklendi`);
  }catch(e){setError(e instanceof Error?e.message:"attachment_upload_failed");}
  finally{setUploadingAttachments(false);if(composerFileRef.current)composerFileRef.current.value="";}
 }

 async function removeComposerAttachment(file:V2InputAttachment){
  if(!activeId||busy||running)return;
  try{
   await agentControlV2Api.deleteAttachment(activeId,file.id);
   setPendingAttachments(
    prev=>prev.filter(
     row=>row.id!==file.id
    )
   );
   setNotice(`Dosya kaldırıldı: ${file.name}`);
   setError(null);
  }catch(e){setError(e instanceof Error?e.message:"attachment_delete_failed");}
 }

 useEffect(()=>{
  const handler=()=>{
   if(busy||running||localModelOptions.length===0)return;
   void createIzloTask();
  };
  window.addEventListener("knsaix:create-izlo-task",handler);
  return ()=>window.removeEventListener("knsaix:create-izlo-task",handler);
 },[busy,running,localModelOptions.length]);

 async function createIzloTask(){
  if(busy||running)return;

  if(localModelOptions.length===0){
   setError(
    "IzloTask için kullanılabilir lokal model yok."
   );
   return;
  }

  setBusy(true);
  setError(null);
  setNotice(null);

  try{
   const localModel=localModelOptions[0];

   const created=
    await agentControlV2Api.createSession({
     title:"IzloTask",
     workspace_cwd:WORKSPACE_CWD,
     memory_scope:"PERSONAL",
     mode:"ACT",
     preferred_model_key:localModel.id,
     execution_profile:"BALANCED",
     execution_scope:"ISOLATED_LOCAL",
    });

   setActiveId(created.session_id);

   setMessages([]);
   setRun(null);
   setTrace([]);
   setPendingAttachments([]);

   await loadSessions();

   setNotice(
    `🔒 IzloTask · ${localModel.id} · local-only`
   );
  }catch(e){
   setError(
    e instanceof Error
     ?e.message
     :"izlotask_create_failed"
   );
  }finally{
   setBusy(false);
  }
 }


 async function sendContent(raw:string,clearDraft:boolean){
  const content=raw.trim();
  if(!content||busy||running)return;

  setBusy(true);
  setNotice(null);

  let submittedSessionId=activeId;
  let optimisticMessageId:string|null=null;

  try{
   let sid=activeId;

   if(!sid){
    const created=await agentControlV2Api.createSession({
     title:titleFrom(content),workspace_cwd:WORKSPACE_CWD,
     memory_scope:"PERSONAL",mode:"ACT",
     preferred_model_key:
      pendingModel==="AUTO"?null:pendingModel,
     execution_profile:pendingProfile,
    });
    sid=created.session_id;
    submittedSessionId=sid;
    setActiveId(sid);
    await loadSessions();
   }

   submittedSessionId=sid;

   // KNSAIX_OPTIMISTIC_USER_MESSAGE_V1
   //
   // A user's message belongs to the conversation immediately.
   // Rendering it must not depend on model/provider success.
   optimisticMessageId=`optimistic-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;

   const optimisticMessage:V2Message={
    id:optimisticMessageId,
    role:"USER",
    content,
    model_key:null,
    provider_key:null,
    created_at:new Date().toISOString(),
    attachments:
     clearDraft
      ?pendingAttachments
      :[],
   };

   setMessages(prev=>[
    ...(prev??[]),
    optimisticMessage,
   ]);

   if(clearDraft){
    setDraft("");
    setModeOverride(null);
   }

   // Canonical continuity invariant: every ordinary turn receives the
   // bounded KNSAIX resume package. Cline execution itself remains fresh.
   await agentControlV2Api.submitMessage(
    sid,
    content,
    selectedProfile==="FAST"
     ?2
     :selectedProfile==="DEEP"
      ?8
      :4,
       clearDraft ? pendingAttachments.map(file=>file.id) : [],
);
   if(clearDraft)setPendingAttachments([]);

   // Replace optimistic state with canonical server state as soon
   // as the request succeeds.
   await refreshActive(sid,true);

   setRun(prev=>prev
    ?{...prev,status:"RUNNING",result_text:null,error_message:null}
    :{
       status:"RUNNING",
       worker_run_id:null,
       result_text:null,
       error_message:null,
       cline_session_id:null,
      }
   );
  }catch(e){
   const submitError=
    e instanceof Error
     ?e.message
     :"submit_failed";

   // The backend may already have durably persisted the USER turn
   // even when model execution later fails. Refresh canonical state
   // first, but preserve the submit failure in the UI.
   if(submittedSessionId){
    try{
     await refreshActive(
      submittedSessionId,
      true,
      true,
     );
    }catch{
     // Keep optimistic state if canonical refresh itself fails.
    }
   }

   setError(submitError);
  }finally{
   setBusy(false);
  }
 }

 async function send(){
  await sendContent(draft,true);
 }

 async function resendMessage(message:V2Message){
  await sendContent(message.content,false);
 }

 function editMessage(message:V2Message){
  if(busy||running)return;
  setDraft(message.content);
  setNotice("Copied to composer — the original message remains in history.");
  window.requestAnimationFrame(()=>{
   composerRef.current?.focus();
   composerRef.current?.setSelectionRange(
    message.content.length,
    message.content.length,
   );
  });
 }

 async function updateComposerPreferences(
  model:string,
  profile:"FAST"|"BALANCED"|"DEEP",
 ){
  if(!activeId){
   setPendingModel(model);
   setPendingProfile(profile);
   return;
  }

  try{
   const result=await agentControlV2Api.updatePreferences(
    activeId,
    {
     preferred_model_key:model==="AUTO"?null:model,
     execution_profile:profile,
    },
   );

   setSessions(prev=>
    (prev??[]).map(row=>
     row.id===activeId
      ?{
        ...row,
        preferred_model_key:result.preferred_model_key,
        execution_profile:result.execution_profile,
       }
      :row
    )
   );

   setError(null);
  }catch(e){
   setError(
    e instanceof Error
     ?e.message
     :"agent_preferences_update_failed"
   );
  }
 }

 async function copyMessage(message:V2Message){
  const ok=await copyTextValue(message.content);
  setNotice(ok?"Copied":"Copy failed");
 }

 async function deleteMessageRow(message:V2Message){
  if(!activeId||busy||running)return;

  if(!window.confirm(
   "Remove this message from the conversation and future resume context?"
  ))return;

  try{
   await agentControlV2Api.deleteMessage(activeId,message.id);
   setMessages(prev=>prev.filter(row=>row.id!==message.id));
   setNotice("Message removed from conversation and future context.");
   setError(null);
  }catch(e){
   setError(e instanceof Error?e.message:"message_delete_failed");
  }
 }

 async function cancel(){
  if(!activeId)return;
  try{await agentControlV2Api.cancel(activeId);await refreshActive(activeId,true);}
  catch(e){setError(e instanceof Error?e.message:"cancel_failed");}
 }

 async function decide(approvalId:string,decision:"ALLOW"|"DENY"){
  try{
   await fetch(`/api/v1/agent-control/v2/approvals/${approvalId}/decide`,{
    method:"POST",credentials:"include",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({decision,reason:decision==="ALLOW"?"Approved in Agent Control":"Denied in Agent Control"}),
   });
   await refreshApprovals();
   if(activeId)await refreshActive(activeId,true);
  }catch(e){setError(e instanceof Error?e.message:"approval_failed");}
 }

 const refreshApprovals=useCallback(async()=>{
  try{setApprovals(await agentControlV2Api.approvals() as ApprovalRow[]);}catch{/* lazy */}
 },[]);

 useEffect(()=>{if(rightOpen&&tab==="Approvals")void refreshApprovals();},[rightOpen,tab,refreshApprovals]);

 async function renameSession(id:string){
 const title=editingTitle.trim();
 if(!title)return;

 try{
  const result=await agentControlV2Api.renameSession(id,title);

  setSessions(prev=>prev?.map(row=>
   row.id===id
    ?{...row,title:result.title,updated_at:new Date().toISOString()}
    :row
  )??prev);

  setEditingId(null);
  setEditingTitle("");
  setError(null);
 }catch(e){
  setError(e instanceof Error?e.message:"rename_failed");
 }
}

 async function togglePin(
  id:string,
  pinned:boolean,
 ){
  try{
   if(pinned){
    await agentControlV2Api.unpinSession(id);
   }else{
    await agentControlV2Api.pinSession(id);
   }

   await loadSessions();
   setError(null);
  }catch(e){
   setError(e instanceof Error?e.message:"pin_failed");
  }
 }

 async function archiveSession(id:string){
  try{
   await agentControlV2Api.archiveSession(id);

   if(activeId===id){
    setActiveId(null);
    setMessages([]);
    setRun(null);
    setTrace([]);
   }

   await loadSessions();
   setError(null);
  }catch(e){
   setError(e instanceof Error?e.message:"archive_failed");
  }
 }

 async function restoreSession(id:string){
  try{
   await agentControlV2Api.restoreSession(id);

   setSessionView("active");
   setActiveId(id);
   setMessages([]);
   setRun(null);
   setTrace([]);
   setError(null);
  }catch(e){
   setError(e instanceof Error?e.message:"restore_failed");
  }
 }

 async function deleteSession(id:string){
  if(!window.confirm(
   "Delete this conversation? It will be tombstoned, not physically erased."
  ))return;

  try{
   await agentControlV2Api.deleteSession(id);

   if(activeId===id){
    setActiveId(null);
    setMessages([]);
    setRun(null);
    setTrace([]);
   }

   await loadSessions();
   setError(null);
  }catch(e){
   setError(e instanceof Error?e.message:"delete_failed");
  }
 }

 function readMessageAloud(message:V2Message){
  if(!("speechSynthesis" in window)){
   setNotice("Read aloud is not supported by this browser.");
   return;
  }
  window.speechSynthesis.cancel();
  const utterance=new SpeechSynthesisUtterance(message.content);
  window.speechSynthesis.speak(utterance);
  setMessageMenuId(null);
 }

 async function regenerateAssistant(message:V2Message){
  const index=messages.findIndex(row=>row.id===message.id);
  if(index<0)return;
  const previous=[...messages.slice(0,index)]
   .reverse()
   .find(row=>row.role==="USER");
  if(!previous){
   setNotice("No previous user message is available to regenerate from.");
   return;
  }
  setMessageMenuId(null);
  await resendMessage(previous);
 }

 function messageTime(value:string){
  try{
   return new Intl.DateTimeFormat(undefined,{
    hour:"2-digit",
    minute:"2-digit",
   }).format(new Date(value));
  }catch{
   return "";
  }
 }

 async function signOut(){
  try{await authApi.logout();}catch{/* best effort */}
  onSignOut();
 }

 async function copyContinuationLink(){
  if(!activeId)return;

  const url=new URL(window.location.href);
  url.searchParams.set("workspace","agent");
  url.searchParams.set("session",activeId);
  url.hash="";

  try{
   if(navigator.clipboard?.writeText){
    await navigator.clipboard.writeText(url.toString());
    setNotice("Continuation link copied.");
   }else{
    window.prompt(
     "Copy continuation link",
     url.toString(),
    );
   }

   setError(null);
  }catch(e){
   setError(
    e instanceof Error
     ?e.message
     :"continuation_link_copy_failed",
   );
  }
 }

 async function shareContinuationLink(){
  if(!activeId)return;

  const url=new URL(window.location.href);
  url.searchParams.set("workspace","agent");
  url.searchParams.set("session",activeId);
  url.hash="";

  const title=
   (sessions??[]).find(
    row=>row.id===activeId,
   )?.title??"KNSAIX Agent Control";

  try{
   if(navigator.share){
    await navigator.share({
     title,
     url:url.toString(),
    });

    setNotice("Continuation link shared.");
   }else if(navigator.clipboard?.writeText){
    await navigator.clipboard.writeText(
     url.toString(),
    );

    setNotice("Continuation link copied.");
   }else{
    window.prompt(
     "Copy continuation link",
     url.toString(),
    );
   }

   setError(null);
  }catch(e){
   if(
    e instanceof DOMException
    &&e.name==="AbortError"
   ){
    return;
   }

   setError(
    e instanceof Error
     ?e.message
     :"continuation_share_failed",
   );
  }
 }

 /* KNSAIX_TASK_ACTIVITY_UI_V1 */
/* KNSAIX_TASK_ACTIVITY_DATETIME_V1 */

function messageDateTime(value:string):string{
 const date=new Date(value);

 if(Number.isNaN(date.getTime())){
  return value;
 }

 return date.toLocaleString(
  undefined,
  {
   year:"numeric",
   month:"2-digit",
   day:"2-digit",
   hour:"2-digit",
   minute:"2-digit",
   second:"2-digit",
  },
 );
}

function activityDateTime(value:string){
 try{
  const raw=String(value??"").trim();

  if(!raw){
   return "";
  }

  /*
   * Backend timestamps are canonical instants.
   *
   * PostgreSQL/ISO timestamps with Z or an explicit offset are
   * parsed directly. A legacy timezone-less ISO timestamp is
   * interpreted as UTC rather than browser-local time so old
   * rows cannot silently jump by the browser timezone offset.
   */
  const hasExplicitZone=
   /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);

  const normalized=
   hasExplicitZone
    ?raw
    :/^\d{4}-\d{2}-\d{2}T/.test(raw)
      ?`${raw}Z`
      :raw;

  const date=new Date(normalized);

  if(Number.isNaN(date.getTime())){
   return "";
  }

  return new Intl.DateTimeFormat(undefined,{
   day:"2-digit",
   month:"2-digit",
   year:"numeric",
   hour:"2-digit",
   minute:"2-digit",
   second:"2-digit",
   hour12:false,
  }).format(date);
 }catch{
  return "";
 }
}

function activityGlyph(
 status:string,
){
 switch(
  String(status).toUpperCase()
 ){
  case "COMPLETED":
   return "✓";

  case "WARNING":
   return "⚠";

  case "FAILED":
   return "✕";

  case "WAITING":
   return "◌";

  case "CANCELLED":
   return "⊘";

  default:
   return "●";
 }
}

async function exportTrace(){
  if(!activeId)return;

  try{
   // Fresh canonical owner-protected trace read at export time.
   const rows=await agentControlV2Api.trace(activeId) as TraceRow[];

   setTrace(rows);

   const session=(sessions??[]).find(
    row=>row.id===activeId,
   )??null;

   const payload={
    schema:"knsaix.agent-control.trace-export.v1",
    exported_at:new Date().toISOString(),
    session:{
     id:activeId,
     title:session?.title??null,
     status:session?.status??null,
     created_at:session?.created_at??null,
     updated_at:session?.updated_at??null,
    },
    trace:rows,
   };

   const blob=new Blob(
    [JSON.stringify(payload,null,2)],
    {type:"application/json"},
   );

   const href=URL.createObjectURL(blob);
   const link=document.createElement("a");

   link.href=href;
   link.download=`knsaix-agent-trace-${activeId}.json`;

   document.body.appendChild(link);
   link.click();
   link.remove();

   window.setTimeout(
    ()=>URL.revokeObjectURL(href),
    0,
   );

   setNotice(`Trace exported (${rows.length} events).`);
   setError(null);
  }catch(e){
   setError(
    e instanceof Error
     ?e.message
     :"trace_export_failed",
   );
  }
 }

 const filtered=(sessions??[]).filter(s=>!search||s.title.toLowerCase().includes(search.toLowerCase()));
 const pendingApprovals=approvals.filter(a=>String(a.status??"").toUpperCase()==="PENDING");

 /* KNSAIX_MAIN_TASK_LIVE_ACTIVITY_V1 */
 const taskActivities=useMemo(
  ()=>trace
   .filter(
    (row):row is TraceRow&{
     activity:TaskActivity
    }=>Boolean(row.activity)
   )
   .map(row=>row.activity),
  [trace],
 );

 const currentActivity=
  taskActivities[0]??null;

 const recentActivities=
  taskActivities.slice(0,4);

const files=useMemo(()=>{
  const seen=new Map<string,string>();
  for(const t of trace){
   const p=t.payload??{};
   const target=typeof p.target==="string"?p.target:null;
   if(!target)continue;
   const at=String(p.action_type??"");
   if(at.includes("read"))seen.set(target,"Read");
   else if(at.includes("created")||at.includes("create"))seen.set(target,"Created");
   else if(at.includes("edit")||at.includes("write")||at.includes("patch"))seen.set(target,"Modified");
  }
  return [...seen.entries()].map(([path,kind])=>({path,kind}));
 },[trace]);

 return (
  <div
   className={`acw${sidebarCollapsed?" sidebar-collapsed":""}${rightOpen?" control-open":""}`}
   data-ux="agent-control-v2-ux-finish-20260829"
   data-shell="knsaix-gpt-final-shell-20260829">
   {(leftOpen||rightOpen)&&(
    <button
     type="button"
     className="acw-backdrop"
     aria-label="Close open panel"
     onClick={()=>{
      setLeftOpen(false);
      setRightOpen(false);
     }}
    />
   )}

  {/* ---------- LEFT: session sidebar ---------- */}
   <AgentSidebar
    leftOpen={leftOpen}
    setLeftOpen={setLeftOpen}
    setSidebarCollapsed={setSidebarCollapsed}
    sessions={sessions}
    filtered={filtered}
    search={search}
    setSearch={setSearch}
    searchOpen={searchOpen}
    setSearchOpen={setSearchOpen}
    searchRef={searchRef}
    sessionView={sessionView}
    setSessionView={setSessionView}
    activeId={activeId}
    setActiveId={setActiveId}
    setMessages={setMessages}
    setRun={setRun}
    setTrace={setTrace}
    statusOpen={statusOpen}
    setStatusOpen={setStatusOpen}
    accountOpen={accountOpen}
    setAccountOpen={setAccountOpen}
    editingId={editingId}
    setEditingId={setEditingId}
    editingTitle={editingTitle}
    setEditingTitle={setEditingTitle}
    renameSession={renameSession}
    togglePin={togglePin}
    archiveSession={archiveSession}
    restoreSession={restoreSession}
    deleteSession={deleteSession}
    copyTextValue={copyTextValue}
    identity={identity}
    activeMode={activeMode}
    run={run}
    messages={messages}
    setNotice={setNotice}
    setError={setError}
    signOut={signOut}
   />

  {/* ---------- CENTER: conversation ---------- */}
  <main className="acw-center">
   <header className="acw-topbar">
     <button
      type="button"
      className="acw-icon acw-menu-toggle kg-top-menu"
      aria-label="Toggle sidebar"
      title="Toggle sidebar"
      onClick={()=>{
       setRightOpen(false);
       setAccountOpen(false);
       setStatusOpen(false);
       if(window.matchMedia("(max-width:820px)").matches){
        setLeftOpen(v=>!v);
       }else{
        setSidebarCollapsed(v=>!v);
       }
      }}
     >☰</button>
     <div className="acw-title-wrap">
      <div className="acw-title">
       {(sessions??[]).find(s=>s.id===activeId)?.title??"Agent Control"}
      </div>
      <div className="acw-title-meta">
       {activeId?`${messages.length} messages`:"Ready"}
      </div>
     </div>
    <div className="acw-chips">
     <span className={`acw-chip mode-${activeMode.toLowerCase()}`}>{activeMode}</span>
      <span
       className={`acw-chip status-${String(run?.status??"idle").toLowerCase()}`}
      >
       AUTO
       {(run?.status&&run.status!=="IDLE")
        ?` → ${run.result_text!==null?"done":run.status.toLowerCase()}`
        :""}
      </span>
    </div>
    <div className="kg-header-right-actions">
     <button
      type="button"
      className="kg-header-action-button kg-header-new-chat"
      aria-label="New chat"
      title="New chat"
      onClick={()=>{
       const target=[
        ...document.querySelectorAll<HTMLButtonElement>("button"),
       ].find(button=>{
        const label=(
         button.getAttribute("aria-label")
         ??button.getAttribute("title")
         ??button.textContent
         ??""
        ).trim();
        return /^(New chat|Yeni sohbet)$/i.test(label);
       });
       if(target && !target.classList.contains("kg-header-new-chat")){
        target.click();
       }
      }}
     >
      ✎
     </button>
     <button
      type="button"
      className="kg-header-action-button acw-icon acw-control-toggle"
      aria-label="Toggle control panel"
      title="Control panel"
      aria-expanded={rightOpen}
      onClick={()=>{
       setLeftOpen(false);
       setRightOpen(v=>!v);
      }}
     >
      ⚙
     </button>
    </div>
   </header>

   {error&&<div className="acw-error" role="alert"><AgentError code={error}/></div>}
    {notice&&(
     <div className="acw-info" role="status" aria-live="polite">
      {notice}
     </div>
    )}

   <div className="acw-flow">
    {messages.length===0&&!running&&(
     <div className="acw-empty">
      <h1>Ajan Kontrolü</h1>
      <p>Bir soru sor, iş planla veya görev tanımla. Doğru mod otomatik seçilir.</p>
     </div>
    )}
    {messages.map(m=>(
     <div key={m.id} className={`acw-msg kg-message ${m.role==="USER"?"user":"assistant"}`}>
      <div className={`acw-bubble ${m.role==="USER"?"user":"assistant"}`}>
       {m.role==="USER"
        ?<div className="acw-rich-text acw-user-text">{m.content}</div>
        :<>
          <MessageInputAttachments attachments={m.attachments}/>
          <MessageBody content={m.content}/>
          <MessageArtifacts artifacts={m.artifacts}/>
         </>
       }
      </div>

       <button
        type="button"
        className="kg-message-timestamp"
        title={messageDateTime(m.created_at)}
        aria-label={
         `Message time ${
          messageDateTime(m.created_at)
         }`
        }
        onClick={()=>
         setExpandedTimeId(
          current=>
           current===m.id
            ?null
            :m.id
         )
        }
        style={{
         alignSelf:
          m.role==="USER"
           ?"flex-end"
           :"flex-start",
         border:0,
         background:"transparent",
         padding:"2px 2px 0",
         fontSize:"11px",
         opacity:.58,
         cursor:"pointer",
         color:"inherit",
         fontVariantNumeric:"tabular-nums",
        }}
       >
        {expandedTimeId===m.id
         ?messageDateTime(m.created_at)
         :messageTime(m.created_at)}
       </button>

      <div className="acw-msg-actions kg-message-actions" aria-label="Message actions">
       <button type="button" className={`kg-message-action ${m.role==="USER"?"kg-mobile-secondary":"kg-mobile-primary"}`} title="Copy" aria-label="Copy message" onClick={()=>void copyMessage(m)}>⧉</button>

       {m.role==="USER"?(
        <>
         <button type="button" className="kg-message-action kg-mobile-primary" title="Edit and resend" aria-label="Edit and resend" disabled={busy||running} onClick={()=>editMessage(m)}>✎</button>
         <button type="button" className="kg-message-action kg-mobile-secondary" title="Resend" aria-label="Resend" disabled={busy||running} onClick={()=>void resendMessage(m)}>↻</button>
        </>
       ):(
        <>
         <button type="button" className="kg-message-action kg-mobile-secondary" title="Read aloud" aria-label="Read aloud" onClick={()=>readMessageAloud(m)}>◖</button>
         <button type="button" className="kg-message-action kg-mobile-secondary" title="Regenerate" aria-label="Regenerate" disabled={busy||running} onClick={()=>void regenerateAssistant(m)}>↻</button>
        </>
       )}

       <div className="kg-message-more-wrap">
        <button
         type="button"
         className="kg-message-action"
         title="More actions"
         aria-label="More actions"
         aria-expanded={messageMenuId===m.id}
         onClick={()=>setMessageMenuId(cur=>cur===m.id?null:m.id)}
        >•••</button>

        {messageMenuId===m.id&&(
         <div className="kg-message-menu">
          <div className="kg-message-menu-time">{messageTime(m.created_at)}</div>
          {m.role!=="USER"&&(
           <button type="button" onClick={()=>readMessageAloud(m)}>◖ Sesli oku</button>
          )}
          <button
           type="button"
           className="danger"
           disabled={busy||running}
           onClick={()=>{
            setMessageMenuId(null);
            void deleteMessageRow(m);
           }}
          >🗑 Sil</button>
         </div>
        )}
       </div>
      </div>
     </div>
    ))}

    {/* KNSAIX_TASK_ACTIVITY_PERSISTENT_RESULT_V1 */}
    {/* KNSAIX_TRACE_OUT_OF_MAIN_CHAT_ONLY_V1 */}
    {false&&(running||taskActivities.length>0)&&(
     <div
      aria-live="polite"
      aria-label="Live task activity"
      style={{
       width:"100%",
       margin:"18px 0 8px",
       padding:"0 4px",
       boxSizing:"border-box",
      }}
     >
      {currentActivity
       ?(
        <div
         style={{
          display:"flex",
          flexDirection:"column",
          gap:"9px",
         }}
        >
         {recentActivities.map(
          (activity,index)=>(
           <div
            key={
             `${
              activity.worker_run_id??"task"
             }-${
              activity.occurred_at
             }-${
              activity.activity_type
             }`
            }
            title={
             [
              activity.detail,
              activity.worker_run_id
               ?`run ${
                 activity.worker_run_id
                }`
               :null,
             ]
              .filter(Boolean)
              .join(" · ")
            }
            style={{
             display:"grid",
             gridTemplateColumns:
              "72px 24px minmax(0,1fr)",
             alignItems:"start",
             columnGap:"6px",
             opacity:index===0?1:0.55,
             fontSize:index===0
              ?"16px"
              :"13px",
             lineHeight:1.35,
            }}
           >
            <time
             dateTime={
              activity.occurred_at
             }
             title={
              new Date(
               activity.occurred_at
              ).toLocaleString()
             }
             style={{
              fontVariantNumeric:
               "tabular-nums",
              whiteSpace:"nowrap",
              opacity:0.8,
             }}
            >
             {activityDateTime(
              activity.occurred_at
             )}
            </time>

            <span
             aria-label={
              activity.status
             }
             style={{
              textAlign:"center",
             }}
            >
             {activityGlyph(
              activity.status
             )}
            </span>

            <span
             style={{
              minWidth:0,
              overflowWrap:"anywhere",
             }}
            >
             <strong
              style={{
               fontWeight:
                index===0?600:500,
              }}
             >
              {activity.label}
             </strong>

             {activity.detail
              &&activity.detail
                !==activity.label
              &&(
               <small
                className="mono"
                style={{
                 display:"block",
                 marginTop:"2px",
                 opacity:0.72,
                 overflowWrap:"anywhere",
                }}
               >
                {activity.detail}
               </small>
              )}
            </span>
           </div>
          )
         )}
        </div>
       )
       :(
        <div
         style={{
          display:"flex",
          alignItems:"center",
          gap:"10px",
          opacity:0.8,
         }}
        >
         <span>◌</span>
         <span>
          Waiting for first activity…
         </span>
        </div>
       )}
     </div>
    )}

    {/* KNSAIX_HIDE_STALE_TASK_FINAL_V1 */}
     {!running
      &&activeMode!=="ASK"
      &&run
      &&String(run.status??"").toUpperCase()!=="IDLE"
      &&(
      <div
       aria-live="polite"
       aria-label="Task final status"
       style={{
        width:"100%",
        boxSizing:"border-box",
        margin:"10px 0 18px",
        padding:"12px 14px",
        border:"1px solid rgba(255,255,255,.12)",
        borderRadius:"12px",
       }}
      >
       <div
        style={{
         display:"flex",
         alignItems:"center",
         gap:"8px",
         marginBottom:"6px",
         fontWeight:600,
        }}
       >
        <span>
         {String(run.status).toUpperCase()==="COMPLETED"
          ?"✓"
          :String(run.status).toUpperCase()==="CANCELLED"
           ?"⊘"
           :"⚠"}
        </span>

        <span>
         {String(run.status).toUpperCase()==="COMPLETED"
          ?"Task completed"
          :String(run.status).toUpperCase()==="CANCELLED"
           ?"Task cancelled"
           :String(run.status).toUpperCase()==="FAILED"
            ?"Task failed"
            :String(run.status).toUpperCase()==="UNREACHABLE"
             ?"Worker unreachable"
             :`Task ${String(run.status).toLowerCase()}`}
        </span>
       </div>

       {run.result_text
        ?(
         <div
          className="acw-rich-text"
          style={{
           whiteSpace:"pre-wrap",
           overflowWrap:"anywhere",
          }}
         >
          {run.result_text}
         </div>
        )
        :String(run.status).toUpperCase()==="COMPLETED"
         ?(
          <div
           style={{
            opacity:.72,
            fontSize:"13px",
           }}
          >
           Execution completed but no final report was received.
          </div>
         )
         :run.error_message
          ?(
           <div
            style={{
             opacity:.82,
             fontSize:"13px",
             overflowWrap:"anywhere",
            }}
           >
            {run.error_message}
           </div>
          )
          :null}
      </div>
     )}

    {pendingApprovals.length>0&&(
     <div className="acw-approval">
      <strong>Agent requests permission</strong>
      {pendingApprovals.slice(0,3).map(a=>(
       <div key={String(a.approval_id)} className="acw-approval-body">
        <p>{String(a.kind??"operation")} — {String(a.reason??"").slice(0,160)}</p>
        <div className="acw-approval-actions">
         <button className="acw-primary" onClick={()=>void decide(String(a.approval_id),"ALLOW")}>Allow</button>
         <button className="acw-danger" onClick={()=>void decide(String(a.approval_id),"DENY")}>Deny</button>
        </div>
       </div>
      ))}
     </div>
    )}
    <div ref={bottomRef}/>
   </div>

   <footer
    className="acw-composer"
    data-product="agent-chatgpt-composer-polish-20260829"
    data-mobile="agent-mobile-final-polish-20260829"
   >
    <div className="acw-composer-shell kg-composer-shell">
     <button
      type="button"
      className="kg-composer-plus"
      aria-label="Add attachment"
      title="Add attachment"
      disabled={busy||running||uploadingAttachments}
      onClick={()=>{
       if(isIzloTask){
        setError(
         "🔒 IzloTask V1 yalnız metin kabul ediyor."
        );
        return;
       }

       composerFileRef.current?.click();
       setError(null);
      }}
     >{uploadingAttachments?"…":"＋"}</button>
     <input
      ref={composerFileRef}
      className="kg-composer-file-input"
      type="file"
      multiple
      tabIndex={-1}
      aria-hidden="true"
      onChange={e=>{void addComposerFiles(e.currentTarget.files);}}
     />
     <textarea
      ref={composerRef}
      rows={1}
      value={draft}
      placeholder="KNSAIX'e mesaj gönder"
      onChange={e=>setDraft(e.target.value)}
      onKeyDown={e=>{
       if(e.key==="Enter"&&!e.shiftKey){
        e.preventDefault();
        void send();
       }
      }}
      disabled={busy||running||uploadingAttachments}
     />
     {running
      ?<button className="acw-stop acw-composer-action kg-send kg-stop" aria-label="Stop" title="Stop" onClick={()=>void cancel()}>Stop</button>
      :<button
        className="acw-send acw-composer-action kg-send" aria-label="Send message" title="Send"
        disabled={busy||uploadingAttachments||!draft.trim()}
        onClick={()=>void send()}
       >
        {busy?"…":"↑"}
       </button>
     }
    </div>
    {pendingAttachments.length>0&&(
      <div className="kg-composer-attachments">
       {pendingAttachments.map(file=>(
        <div className="kg-composer-attachment" key={file.id} title={`${file.mime_type} · ${file.size} bytes`}>
         <span className="kg-composer-attachment-name">{file.name}</span>
         <button
          type="button"
          className="kg-composer-attachment-remove"
          title="Remove attachment"
          aria-label={`Remove ${file.name}`}
          onPointerDown={event=>{
           event.preventDefault();
           event.stopPropagation();
           void removeComposerAttachment(file);
          }}
         >
          ×
         </button>
        </div>
       ))}
      </div>
     )}
    <div className="kg-composer-controls kg-composer-controls-custom">
     <details className="kg-composer-menu kg-profile-menu" onMouseLeave={e=>e.currentTarget.removeAttribute("open")}>
      <summary
       className="kg-composer-mini-control"
       aria-label="Hız"
       title="Hız"
      >
       <span className="kg-ctl-icon kg-profile-state-icon">
        {selectedProfile==="FAST"
         ?"F"
         :selectedProfile==="DEEP"
          ?"H"
          :"M"}
       </span>
      </summary>
      <div className="kg-composer-menu-popover" role="listbox" aria-label="Hız">
       {(["FAST","BALANCED","DEEP"] as const).map(profile=>(
        <button
         key={profile}
         type="button"
         role="option"
         aria-selected={selectedProfile===profile}
         className={selectedProfile===profile?"active":""}
         disabled={busy||running}
         onClick={e=>{
          const details=e.currentTarget.closest("details");
          details?.removeAttribute("open");
          void updateComposerPreferences(
           selectedModel,
           profile,
          );
         }}
        >
         <span>
          {profile==="FAST"
           ?"Fast"
           :profile==="DEEP"
            ?"High"
            :"Medium"}
         </span>
         {selectedProfile===profile&&<small>✓</small>}
        </button>
       ))}
      </div>
     </details>

     <details className="kg-composer-menu kg-model-menu" onMouseLeave={e=>e.currentTarget.removeAttribute("open")}>
      <summary
       className="kg-composer-mini-control"
       aria-label="Model"
       title="Model"
      >
       <span className="kg-ctl-label kg-model-state-label">
        {isIzloTask
         ?(selectedModel==="AUTO"
           ?(localModelOptions[0]?.id??"local")
           :selectedModel)
         :selectedModel}
       </span>
      </summary>

      <div className="kg-composer-menu-popover kg-model-popover" role="listbox" aria-label="Model">
       {isIzloTask?(
        localModelOptions.length===0?(
         <div className="kg-composer-menu-empty">
          Lokal model yok
         </div>
        ):(
         localModelOptions.map(model=>(
          <button
           key={model.id}
           type="button"
           role="option"
           aria-selected={
            selectedModel==="AUTO"
             ?model.id===localModelOptions[0]?.id
             :selectedModel===model.id
           }
           className={
            selectedModel==="AUTO"
             ?(model.id===localModelOptions[0]?.id?"active":"")
             :(selectedModel===model.id?"active":"")
           }
           disabled={busy||running}
           onClick={e=>{
            const details=e.currentTarget.closest("details");
            details?.removeAttribute("open");
            void updateComposerPreferences(
             model.id,
             selectedProfile,
            );
           }}
          >
           <span>🔒 {model.id}</span>
           {(selectedModel==="AUTO"
             ?model.id===localModelOptions[0]?.id
             :selectedModel===model.id)&&<small>✓</small>}
          </button>
         ))
        )
       ):(
        <>
         <button
          type="button"
          role="option"
          aria-selected={selectedModel==="AUTO"}
          className={selectedModel==="AUTO"?"active":""}
          disabled={busy||running}
          onClick={e=>{
           const details=e.currentTarget.closest("details");
           details?.removeAttribute("open");
           void updateComposerPreferences(
            "AUTO",
            selectedProfile,
           );
          }}
         >
          <span>AUTO</span>
          {selectedModel==="AUTO"&&<small>✓</small>}
         </button>

         {modelOptions
          .filter(m=>m.id.toUpperCase()!=="AUTO")
          .map(model=>(
           <button
            key={model.id}
            type="button"
            role="option"
            aria-selected={selectedModel===model.id}
            className={selectedModel===model.id?"active":""}
            disabled={busy||running}
            onClick={e=>{
             const details=e.currentTarget.closest("details");
             details?.removeAttribute("open");
             void updateComposerPreferences(
              model.id,
              selectedProfile,
             );
            }}
           >
            <span>{model.id}</span>
            {selectedModel===model.id&&<small>✓</small>}
           </button>
          ))}
        </>
       )}
      </div>
     </details>
    </div>

    <div className="acw-composer-meta">
     <small>Enter to send · Shift+Enter for newline</small>
    </div>
   </footer>
  </main>

  {/* ---------- RIGHT: control panel ---------- */}
  <aside className={`acw-right${rightOpen?" open":""}`}>
    <div className="acw-panel-head">
     <div>
      <strong>Control center</strong>
      <small>{run?.status??"IDLE"}</small>
     </div>
     <button
      type="button"
      className="acw-icon acw-close"
      aria-label="Close control panel"
      title="Close control panel"
      onClick={()=>setRightOpen(false)}
     >
      ×
     </button>
    </div>
   <div className="acw-tabs">
    {(["Run","Trace","Files","Context","Approvals"] as RightTab[]).map(t=>(
     <button key={t} className={`acw-tab${tab===t?" active":""}`}
      onClick={()=>setTab(t)}>
       <span>{t}</span>
       {t==="Approvals"&&pendingApprovals.length>0&&(
        <span className="acw-tab-badge">
         {pendingApprovals.length}
        </span>
       )}
     </button>
    ))}
   </div>

   <div className="acw-panel">
    {tab==="Run"&&(
     <dl className="acw-kv">
      <dt>State</dt><dd>{run?.status??"IDLE"}</dd>
      <dt>Mode</dt><dd>{activeMode}</dd>
      <dt>Routing</dt><dd>AUTO</dd>
      <dt>Provider</dt><dd>{run?.cline_session_id?"cline":"—"}</dd>
      <dt>Model</dt>
      <dd>
       {isIzloTask?"🔒 ":""}
       {(sessions??[]).find(s=>s.id===activeId)?.model_key??"AUTO"}
      </dd>
      <dt>Workspace</dt><dd className="mono">{WORKSPACE_CWD}</dd>
      <dt>Cline session</dt><dd className="mono">{run?.cline_session_id??"—"}</dd>
      {run?.error_message&&<><dt>Last error</dt><dd className="bad">{run.error_message}</dd></>}
     </dl>
    )}

    {tab==="Trace"&&(
     <>
      <div className="acw-panel-actions">
       <button
        type="button"
        className="acw-secondary"
        disabled={!activeId}
        onClick={()=>void exportTrace()}
       >
        Export JSON
       </button>
      </div>

      {trace.filter(
       t=>Boolean(t.activity)
      ).length===0
       ?(
        <p className="acw-muted">
         No task activity recorded yet.
        </p>
       )
       :(
        <ul
         className="acw-trace"
         aria-label="Task activity timeline"
        >
         {[...trace]
          .reverse()
          .filter(
           t=>Boolean(t.activity)
          )
          .map(t=>{
           const activity=t.activity!;

           return (
            <li
             key={t.id}
             className={
              `acw-trace-row activity-${String(
               activity.status
              ).toLowerCase()}`
             }
             title={
              [
               activity.detail,
               activity.worker_run_id
                ?`run ${
                  activity.worker_run_id
                 }`
                :null,
              ]
               .filter(Boolean)
               .join(" · ")
             }
            >
             <small
              className="acw-activity-time"
              title={
               new Date(
                activity.occurred_at
               ).toLocaleString()
              }
             >
               {/* KNSAIX_ACTIVITY_VISIBLE_DATETIME_V2 */}
               {new Date(
                activity.occurred_at
               ).toLocaleString()}
              </small>

             <span
              className="acw-trace-op"
              aria-label={
               activity.status
              }
             >
              {activityGlyph(
               activity.status
              )}
             </span>

             <span
              className="acw-activity-copy"
             >
              <strong>
               {activity.label}
              </strong>

              {activity.detail
               &&(
                <small
                 className={
                  "mono "
                  +"acw-activity-detail"
                 }
                >
                 {activity.detail}
                </small>
               )}
             </span>
            </li>
           );
          })}
        </ul>
       )}
     </>
    )}

    {tab==="Files"&&(
     files.length===0
      ?<p className="acw-muted">No file activity recorded for this run.</p>
      :<ul className="acw-files">
        {files.map(f=>(
         <li key={f.path}>
          <span className={`acw-file-kind ${f.kind.toLowerCase()}`}>{f.kind}</span>
          <span className="mono">{f.path}</span>
         </li>
        ))}
       </ul>
    )}

    {tab==="Context"&&(
     <>
      <dl className="acw-kv">
       <dt>Project</dt><dd>KNSAIX (canonical default)</dd>
       <dt>Memory scope</dt><dd>PERSONAL</dd>
       <dt>Worker run</dt><dd className="mono">{run?.worker_run_id??"—"}</dd>
       <dt>Resume semantics</dt><dd>bounded canonical continuation</dd>
       <dt>Session</dt><dd className="mono">{activeId??"—"}</dd>
      </dl>

      <div className="acw-panel-actions">
       <button
        type="button"
        className="acw-secondary"
        disabled={!activeId}
        onClick={()=>void copyContinuationLink()}
       >
        Copy continuation link
       </button>
        <button
         type="button"
         className="acw-secondary"
         disabled={!activeId}
         onClick={()=>void shareContinuationLink()}
        >
         Share
        </button>
      </div>
     </>
    )}

    {tab==="Approvals"&&(
     pendingApprovals.length===0
      ?<p className="acw-muted">No pending approvals.</p>
      :<ul className="acw-approvals-list">
        {pendingApprovals.map(a=>(
         <li key={String(a.approval_id)}>
          <strong>{String(a.kind??"operation")}</strong>
          <p>{String(a.reason??"")}</p>
          <div>
           <button className="acw-primary" onClick={()=>void decide(String(a.approval_id),"ALLOW")}>Allow</button>
           <button className="acw-danger" onClick={()=>void decide(String(a.approval_id),"DENY")}>Deny</button>
          </div>
         </li>
        ))}
       </ul>
    )}
   </div>
  </aside>
 </div>
 );
}

